import * as Intercom from '@lib/intercom'
import * as constants from '@constants'
import {createWithEqualityFn} from 'zustand/traditional'
import {shallow} from 'zustand/shallow'
import {devtools, persist} from 'zustand/middleware'
import {datadogRum} from '@datadog/browser-rum'
import {omit} from 'lodash-es'
import config from '@lib/config'

export const useAuth = createWithEqualityFn(
  devtools(
    persist(
      (set, get) => ({
        me: null,
        mePreferencesUpdated: false,
        accessToken: null,
        sessionToken: null,
        auditCompanyId: null,
        companyId: null,
        impersonateId: null,
        refreshToken: null,
        sessionStartedAt: null,
        isSessionExpired: null,
        login: state => {
          Intercom.identify(state.me?.unImpersonatedUser || state.me)

          return set({
            me: state.me,
            accessToken: config.isCookieEnabled ? null : state.accessToken,
            refreshToken: config.isCookieEnabled ? null : state.refreshToken,
            sessionToken: config.isCookieEnabled ? null : state.sessionToken,
            sessionStartedAt: Date.now(),
            isSessionExpired: false,
          })
        },
        meReceived: state => {
          if (state?.me?.unImpersonatedUser?.id) {
            Intercom.identify(state?.me?.unImpersonatedUser)
          }

          return set({
            me: {
              ...get().me,
              ...state,
              unImpersonatedUser: {
                ...get().me?.unImpersonatedUser,
                ...state.unImpersonatedUser,
              },
            },
            mePreferencesUpdated: state?.mePreferencesUpdated || false,
          })
        },
        accessTokenReceived: (accessToken, user) => {
          if (user) Intercom.identify(user)
          set({
            accessToken: config.isCookieEnabled ? null : accessToken,
            sessionStartedAt: Date.now(),
            isSessionExpired: false,
          })
        },
        sessionExpired: () => set({isSessionExpired: true}),
        impersonate: state => {
          set({
            impersonateId: state.id,
          })

          return set({
            me: {...state, unImpersonatedUser: get().me},
          })
        },
        companySwitch: company => set({companyId: company?.companyId}),
        auditing: key => set({auditCompanyId: key}),
        stopAuditing: () => set({auditCompanyId: null}),
        logout: () => {
          Intercom.logout()
          datadogRum.clearUser()

          return set({
            me: null,
            accessToken: null,
            auditCompanyId: null,
            companyId: null,
            impersonateId: null,
            refreshToken: null,
            sessionToken: null,
            sessionStartedAt: null,
            isSessionExpired: null,
          })
        },
        stopImpersonate: () => {
          return set({
            impersonateId: null,
            auditCompanyId: null,
            companyId: null,
            me: {...get().me?.unImpersonatedUser},
          })
        },
        isFeatureEnabled: featureId => {
          return Boolean(get().me?.company?.enabledFeatures?.some(enabledFeature => enabledFeature.id === featureId))
        },
        developmentTag: () => {
          return get().me?.developmentTag
        },
        isDevelopmentTagEnabled: featureId => {
          return get().me?.enabledDevelopmentTags?.find(tag => tag.featureId === featureId)
        },
        isOwnCompany: companyId => Boolean(get().me?.company?.id === companyId),
        isAdmin: () => {
          return Boolean(
            get().me?.role?.includes('RETRACED_') && get().me.company?.id === config.retracedAdminCompanyId
          )
        },
        isRetailer: () => {
          return Boolean(get().me?.company?.facilityProcesses?.split(',').includes('RETAILER'))
        },
        isAuditor: () => {
          return Boolean(get().me?.company?.facilityProcesses?.split(',').includes('AUDITOR') || get().auditCompanyId)
        },
        isSupplier: () => {
          const facilityProcesses = get().me?.company?.facilityProcesses?.split(',') || []
          return Boolean(facilityProcesses.length > 0 && !facilityProcesses.includes('RETAILER') && !facilityProcesses.includes('AUDITOR'))
        },
        isImpersonating: () => {
          return Boolean(get().me?.unImpersonatedUser?.id && get().me?.id !== get().me?.unImpersonatedUser?.id)
        },
        isAuditing: () => Boolean(get().me && get().auditCompanyId),
        isRiskAnalysisAvailable: company => {
          return Boolean(
            get().me?.company?.enabledFeatures?.some(enabledFeature => enabledFeature.id === 'risk-analysis') &&
              company?.extend?.connectionType === constants.connectionType.enumMapping.SUPPLIER
          )
        },
      }),
      {
        name: 'retraced:context',
        partialize: state => (config.environment === 'development' ? state : omit(state, 'me')),
      }
    ),
    {enabled: config.environment === 'development' && Boolean(window.__REDUX_DEVTOOLS_EXTENSION__)}
  ),
  shallow
)
